/**
 * KAN-799: расход токенов OpenRouter считается по ключу платформы. Чистые функции:
 * модель графика по дням (день без данных по ключу не входит в максимум и суммы)
 * и подписи статьи токенов по области расхода (scope). Без scope (старый backend)
 * подписей нет, вид прежний. Список моделей за месяц: строки для таблицы под графиком.
 */
import { formatRub, formatUsd } from '@/lib/money';
import type { ExpenseItem, TokensDay, TokensModel, TokensScope } from '@/types/expenses';

/** Высота столбца в процентах от максимума; ненулевое значение видно (не меньше 2%). */
export function barHeightPercent(value: number, max: number): number {
  if (max <= 0 || value <= 0) return 0;
  return Math.max(2, Math.round((value / max) * 100));
}

export const NO_KEY_DATA = 'нет данных по ключу';

export interface TokensBar {
  /** YYYY-MM-DD. */
  date: string;
  /** День месяца, "08". */
  day: string;
  /** false: за день нет данных по ключу, столбец пустой. */
  hasData: boolean;
  heightPercent: number;
  /** Подсказка и aria-label столбца. */
  title: string;
}

export interface TokensChartModel {
  bars: TokensBar[];
  /** Максимум и суммы только по дням с данными. */
  maxUsd: number;
  totalUsd: number;
  totalRub: number;
  /** Число дней без данных по ключу. */
  missingDays: number;
  firstDay: string;
  lastDay: string;
}

const isNumber = (v: number | null | undefined): v is number =>
  typeof v === 'number' && Number.isFinite(v);

const dayOf = (date: string): string => date.slice(8, 10);

function barTitle(d: TokensDay): string {
  if (!isNumber(d.usd)) return `${d.date}: ${NO_KEY_DATA}`;
  const amounts = [formatUsd(d.usd)];
  if (isNumber(d.rub)) amounts.push(formatRub(d.rub));
  return `${d.date}: ${amounts.join(', ')}`;
}

/** Модель столбчатого графика токенов по дням. */
export function tokensChartModel(days: TokensDay[]): TokensChartModel {
  const withData = days.filter((d) => isNumber(d.usd));
  const maxUsd = withData.reduce((max, d) => Math.max(max, d.usd as number), 0);
  const totalUsd = withData.reduce((sum, d) => sum + (d.usd as number), 0);
  const totalRub = withData.reduce((sum, d) => sum + (isNumber(d.rub) ? d.rub : 0), 0);
  const bars = days.map((d): TokensBar => {
    const hasData = isNumber(d.usd);
    return {
      date: d.date,
      day: dayOf(d.date),
      hasData,
      heightPercent: hasData ? barHeightPercent(d.usd as number, maxUsd) : 0,
      title: barTitle(d),
    };
  });
  return {
    bars,
    maxUsd,
    totalUsd,
    totalRub,
    missingDays: days.length - withData.length,
    firstDay: days.length ? dayOf(days[0].date) : '',
    lastDay: days.length ? dayOf(days[days.length - 1].date) : '',
  };
}

interface ScopeText {
  /** Подпись в карточке статьи на странице расходов. */
  label: string;
  /** Начало части "токены ..." в строке дашборда. */
  summaryPrefix: string;
}

const SCOPE_TEXT: Record<TokensScope, ScopeText> = {
  key: { label: 'по ключу платформы', summaryPrefix: 'токены по ключу' },
  account: {
    label: 'весь счёт OpenRouter',
    summaryPrefix: 'токены (весь счёт)',
  },
};

function scopeText(item: ExpenseItem | undefined): ScopeText | null {
  const scope = item?.scope;
  return scope && Object.prototype.hasOwnProperty.call(SCOPE_TEXT, scope)
    ? SCOPE_TEXT[scope]
    : null;
}

/** Подпись области расхода в карточке статьи; без scope: null. */
export function tokensScopeLabel(item: ExpenseItem | undefined): string | null {
  return scopeText(item)?.label ?? null;
}

/** Начало части про токены в компактной строке дашборда; без scope: "токены". */
export function tokensSummaryPrefix(item: ExpenseItem | undefined): string {
  return scopeText(item)?.summaryPrefix ?? 'токены';
}

/**
 * Справочная строка "весь счёт OpenRouter: $X": только при расходе по ключу,
 * когда сумма счёта пришла и больше расхода ключа.
 */
export function tokensAccountLine(item: ExpenseItem | undefined): string | null {
  if (!item || item.scope !== 'key') return null;
  const account = item.account_amount_usd;
  if (!isNumber(account) || account <= (item.amount_usd ?? 0)) return null;
  return `весь счёт OpenRouter: ${formatUsd(account)}`;
}

export interface TokensModelRow {
  model: string;
  /** "$4.00". */
  usd: string;
  /** "400 ₽"; рублей в ответе нет: пустая строка. */
  rub: string;
  requests: number;
}

/**
 * Строки списка моделей за месяц из статьи токенов. Порядок как в ответе (по убыванию суммы).
 * Поля нет (старый backend или нет истории) или запись без модели и суммы: строки нет.
 */
export function tokensModelRows(item: ExpenseItem | undefined): TokensModelRow[] {
  const models: TokensModel[] = Array.isArray(item?.models) ? item.models : [];
  return models
    .filter((m) => m && typeof m.model === 'string' && m.model && isNumber(m.amount_usd))
    .map((m) => ({
      model: m.model,
      usd: formatUsd(m.amount_usd),
      rub: isNumber(m.amount_rub) ? formatRub(m.amount_rub) : '',
      requests: isNumber(m.requests) ? m.requests : 0,
    }));
}

/**
 * Подпись неполного месяца рядом с подписью области расхода: "данные ключа с 12.09".
 * Поля partial_from нет или оно не дата: null.
 */
export function tokensPartialLabel(item: ExpenseItem | undefined): string | null {
  const match = /^\d{4}-(\d{2})-(\d{2})$/.exec(item?.partial_from ?? '');
  return match ? `данные ключа с ${match[2]}.${match[1]}` : null;
}
